/**
 * PartyRegisterView.tsx — Party-wise Sales / Purchase Register
 * Shows consolidated party totals with drill-down to individual vouchers.
 * Keyboard: ↑↓ navigate, Enter drill-down, Tab switch Sales/Purchase, Esc back.
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import { useLiveQuery } from 'dexie-react-hooks';

const fmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

type Mode = 'Sales' | 'Purchase';

export const PartyRegisterView = ({ onQuit }: { onQuit: () => void }) => {
  const activeCompany = useStore(s => s.activeCompany);
  const fromDate      = useStore(s => s.fromDate);
  const toDate        = useStore(s => s.toDate);
  const [mode, setMode]           = useState<Mode>('Sales');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [drillParty, setDrillParty]   = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const allData = useLiveQuery(async () => {
    if (!activeCompany) return null;
    const types = mode === 'Sales' ? ['Sales', 'Credit Note'] : ['Purchase', 'Debit Note'];
    const vouchers = await db.vouchers
      .where('companyId').equals(activeCompany.id!)
      .filter(v => types.includes(v.type) && v.date >= fromDate && v.date <= toDate && !v.deleted)
      .toArray();
    const vIds = vouchers.map(v => v.id!);
    if (!vIds.length) return { partyMap: new Map(), vouchers: [] };

    const entries  = await db.voucherEntries.where('voucherId').anyOf(vIds).toArray();
    const ledgers  = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
    const groups   = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
    const ledgerMap = new Map(ledgers.map(l => [l.id!, l]));
    const groupMap  = new Map(groups.map(g => [g.id!, g.name]));
    const PARTY_GROUPS = ['Sundry Debtors', 'Sundry Creditors', 'Cash-in-Hand', 'Bank Accounts'];

    const entriesByV = new Map<number, typeof entries>();
    entries.forEach(e => {
      if (!entriesByV.has(e.voucherId)) entriesByV.set(e.voucherId, []);
      entriesByV.get(e.voucherId)!.push(e);
    });

    const partyMap = new Map<string, { invoices: number; taxable: number; tax: number; total: number; vouchers: any[] }>();

    vouchers.forEach(v => {
      const vEntries = entriesByV.get(v.id!) || [];
      let taxable = 0, tax = 0, partyName = '';
      vEntries.forEach(e => {
        const l = ledgerMap.get(e.ledgerId);
        if (!l) return;
        const grp = l.groupId ? groupMap.get(l.groupId) : '';
        if (PARTY_GROUPS.includes(grp || '')) { partyName = l.name; return; }
        if (['CGST','SGST','IGST','CESS'].includes(l.name)) tax += e.amount;
        else if (['Sales Accounts','Direct Incomes','Indirect Incomes','Purchase Accounts','Direct Expenses'].includes(grp || ''))
          taxable += e.amount;
      });
      if (!partyName) partyName = 'Unknown Party';
      const total = taxable + tax;
      if (!partyMap.has(partyName)) partyMap.set(partyName, { invoices: 0, taxable: 0, tax: 0, total: 0, vouchers: [] });
      const p = partyMap.get(partyName)!;
      p.invoices += 1; p.taxable += taxable; p.tax += tax; p.total += total;
      p.vouchers.push({ date: v.date, ref: v.referenceNumber || v.voucherNumber, type: v.type, taxable, tax, total });
    });

    return { partyMap, vouchers };
  }, [activeCompany, fromDate, toDate, mode]);

  const partyRows = useMemo(() => {
    if (!allData?.partyMap) return [];
    return Array.from(allData.partyMap.entries())
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.total - a.total);
  }, [allData]);

  const drillRows = useMemo(() => {
    if (!drillParty || !allData?.partyMap) return [];
    return (allData.partyMap.get(drillParty)?.vouchers || []).sort((a: any, b: any) => b.date.localeCompare(a.date));
  }, [drillParty, allData]);

  const totals = useMemo(() => ({
    invoices: partyRows.reduce((s, r) => s + r.invoices, 0),
    taxable:  partyRows.reduce((s, r) => s + r.taxable,  0),
    tax:      partyRows.reduce((s, r) => s + r.tax,       0),
    total:    partyRows.reduce((s, r) => s + r.total,     0),
  }), [partyRows]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { if (drillParty) { setDrillParty(null); } else { onQuit(); } return; }
    if (e.key === 'Tab') { e.preventDefault(); setMode(m => m === 'Sales' ? 'Purchase' : 'Sales'); setSelectedIdx(0); setDrillParty(null); return; }
    if (!drillParty) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, partyRows.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && partyRows[selectedIdx]) { setDrillParty(partyRows[selectedIdx].name); }
    } else {
      if (e.key === 'ArrowLeft' || e.key === 'Backspace') { setDrillParty(null); }
    }
  }, [drillParty, onQuit, partyRows, selectedIdx]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Auto-scroll to selected row
  useEffect(() => {
    const el = scrollRef.current?.querySelector(`[data-idx="${selectedIdx}"]`) as HTMLElement;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const th = 'px-2 py-1.5 text-left font-bold text-[11px] border-b border-[#aebec8] bg-[#f0f9f9] whitespace-nowrap sticky top-0';
  const thr = `${th} text-right`;
  const td = 'px-2 py-[3px] border-b border-gray-100 text-[11px] whitespace-nowrap';
  const tdr = `${td} text-right tabular-nums`;

  const modeColor = mode === 'Sales' ? 'text-[#1e8585]' : 'text-orange-600';

  return (
    <div className="flex flex-col w-full h-full bg-white z-20 absolute inset-0 font-sans text-[12px] text-black">
      {/* Title */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm shrink-0">
        <span className="w-1/3">Party-wise {mode} Register</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={onQuit}>✕  [Esc]</span>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 px-2 py-1.5 border-b border-[#aebec8] bg-white shrink-0">
        <div className="flex border border-[#aebec8] rounded overflow-hidden">
          {(['Sales', 'Purchase'] as Mode[]).map(m => (
            <button key={m}
              className={`px-4 py-[3px] text-[11px] font-bold ${mode === m ? 'bg-[#1e8585] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => { setMode(m); setSelectedIdx(0); setDrillParty(null); }}>
              {m}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-gray-400">{fromDate} → {toDate}</span>
        <span className="ml-auto text-[10px] text-gray-400">Tab: Switch • ↑↓: Navigate • Enter: Drill-down • Esc: Back</span>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-px bg-[#aebec8] border-b border-[#aebec8] shrink-0">
        {[
          { label: 'Parties',    val: partyRows.length, isCount: true },
          { label: 'Invoices',   val: totals.invoices,  isCount: true },
          { label: 'Taxable',    val: totals.taxable,   isCount: false },
          { label: 'Grand Total',val: totals.total,     isCount: false },
        ].map(c => (
          <div key={c.label} className="bg-white px-3 py-1.5 text-center">
            <div className="text-[9px] text-gray-500 font-semibold uppercase tracking-wide">{c.label}</div>
            <div className={`font-bold tabular-nums text-[12px] ${modeColor}`}>
              {c.isCount ? c.val : `₹${fmt(c.val)}`}
            </div>
          </div>
        ))}
      </div>

      {/* Drill-down breadcrumb */}
      {drillParty && (
        <div className="px-2 py-1 bg-[#f0fafa] border-b border-[#cde8e8] text-[11px] flex items-center gap-2 shrink-0">
          <button onClick={() => setDrillParty(null)} className="text-[#1e8585] hover:underline font-semibold">
            ← All Parties
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-bold text-gray-700">{drillParty}</span>
          <span className="text-gray-400 ml-auto">[Esc or ← to go back]</span>
        </div>
      )}

      {/* Table */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        {!drillParty ? (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>#</th>
                <th className={th}>Party Name</th>
                <th className={thr}>Invoices</th>
                <th className={thr}>Taxable (₹)</th>
                <th className={thr}>Tax (₹)</th>
                <th className={thr}>Total (₹)</th>
                <th className={thr}>% of Total</th>
              </tr>
            </thead>
            <tbody>
              {partyRows.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-gray-400 py-10">No {mode} vouchers in this period.</td></tr>
              ) : partyRows.map((r, i) => {
                const isSel = i === selectedIdx;
                return (
                  <tr key={r.name} data-idx={i}
                    className={`cursor-pointer ${isSel ? 'bg-[#1e8585] text-white' : i % 2 === 0 ? 'bg-white hover:bg-[#f0fafa]' : 'bg-[#f8fcfc] hover:bg-[#f0fafa]'}`}
                    onClick={() => { setSelectedIdx(i); setDrillParty(r.name); }}>
                    <td className={td}>{i + 1}</td>
                    <td className={`${td} font-semibold`}>{r.name}</td>
                    <td className={`${tdr} ${isSel ? 'text-white' : ''}`}>{r.invoices}</td>
                    <td className={`${tdr} ${isSel ? 'text-white' : ''}`}>{fmt(r.taxable)}</td>
                    <td className={`${tdr} ${isSel ? 'text-white' : ''}`}>{r.tax > 0 ? fmt(r.tax) : '—'}</td>
                    <td className={`${tdr} font-bold ${isSel ? 'text-white' : modeColor}`}>{fmt(r.total)}</td>
                    <td className={`${tdr} ${isSel ? 'text-white/80' : 'text-gray-500'}`}>
                      {totals.total > 0 ? ((r.total / totals.total) * 100).toFixed(1) + '%' : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {partyRows.length > 0 && (
              <tfoot>
                <tr className="bg-[#f0fafa] font-bold border-t-2 border-[#aebec8]">
                  <td className={th} colSpan={2}>Total ({partyRows.length} parties)</td>
                  <td className={thr}>{totals.invoices}</td>
                  <td className={thr}>{fmt(totals.taxable)}</td>
                  <td className={thr}>{fmt(totals.tax)}</td>
                  <td className={`${thr} text-[#1e8585]`}>{fmt(totals.total)}</td>
                  <td className={thr}>100%</td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          /* Drill-down voucher list */
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>Date</th>
                <th className={th}>Ref / Invoice No.</th>
                <th className={th}>Type</th>
                <th className={thr}>Taxable (₹)</th>
                <th className={thr}>Tax (₹)</th>
                <th className={thr}>Total (₹)</th>
              </tr>
            </thead>
            <tbody>
              {drillRows.map((r: any, i: number) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8fcfc]'}>
                  <td className={td}>{r.date}</td>
                  <td className={`${td} font-mono text-[10px]`}>{r.ref || '—'}</td>
                  <td className={td}>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                      r.type === 'Credit Note' ? 'bg-orange-50 text-orange-700' :
                      r.type === 'Debit Note'  ? 'bg-purple-50 text-purple-700' :
                                                 'bg-teal-50 text-teal-700'}`}>
                      {r.type}
                    </span>
                  </td>
                  <td className={tdr}>{fmt(r.taxable)}</td>
                  <td className={tdr}>{r.tax > 0 ? fmt(r.tax) : '—'}</td>
                  <td className={`${tdr} font-bold ${modeColor}`}>{fmt(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#f0fafa] font-bold border-t-2 border-[#aebec8]">
                <td className={th} colSpan={3}>Total ({drillRows.length} vouchers)</td>
                <td className={thr}>{fmt(drillRows.reduce((s: number, r: any) => s + r.taxable, 0))}</td>
                <td className={thr}>{fmt(drillRows.reduce((s: number, r: any) => s + r.tax, 0))}</td>
                <td className={`${thr} ${modeColor}`}>{fmt(drillRows.reduce((s: number, r: any) => s + r.total, 0))}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};
